import type { ReactNode } from "react";

/**
 * Markdown-lite for chat bodies: ```code blocks```, `inline code`, **bold**, *italic* / _italic_,
 * [text](url), bare http(s) links and @mentions. Everything else is literal text.
 *
 * Safety: the parser produces a small tree that is rendered as React elements (text is always a
 * text node; there is no HTML path), and a link is a link only when its scheme is http, https or
 * mailto. `<script>`, `javascript:` and friends therefore show up as the characters typed.
 */

export type Inline =
  | { t: "text"; v: string }
  | { t: "code"; v: string }
  | { t: "strong"; c: Inline[] }
  | { t: "em"; c: Inline[] }
  | { t: "link"; href: string; text: string }
  | { t: "mention"; handle: string };

export type Block = { t: "pre"; v: string } | { t: "p"; c: Inline[] };

const SAFE_SCHEMES = new Set(["http:", "https:", "mailto:"]);

/** The URL if it is absolute and uses an allowed scheme, else null. */
export function safeHref(raw: string): string | null {
  try {
    const url = new URL(raw);
    return SAFE_SCHEMES.has(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

const INLINE = new RegExp(
  [
    "`(?<code>[^`\\n]+)`",
    "\\[(?<ltext>[^\\]\\n]+)\\]\\((?<lhref>[^)\\s]+)\\)",
    "(?<url>\\bhttps?:\\/\\/[^\\s<>()]*[^\\s<>().,;:!?'\"])",
    "\\*\\*(?<strong>\\S(?:[^\\n]*?\\S)?)\\*\\*",
    "(?<![\\w*])\\*(?<em1>[^*\\s](?:[^*\\n]*?[^*\\s])?)\\*(?![\\w*])",
    "(?<![\\w_])_(?<em2>[^_\\s](?:[^_\\n]*?[^_\\s])?)_(?![\\w_])",
    "(?<![\\w@])@(?<mention>[\\p{L}\\p{N}._-]*[\\p{L}\\p{N}_-])",
  ].join("|"),
  "gu",
);

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  const pushText = (v: string) => {
    if (v.length === 0) return;
    const last = out.at(-1);
    if (last?.t === "text") last.v += v;
    else out.push({ t: "text", v });
  };
  let at = 0;
  for (const m of text.matchAll(INLINE)) {
    const g = m.groups;
    if (g === undefined) continue;
    pushText(text.slice(at, m.index));
    at = m.index + m[0].length;
    if (g["code"] !== undefined) out.push({ t: "code", v: g["code"] });
    else if (g["lhref"] !== undefined && g["ltext"] !== undefined) {
      const href = safeHref(g["lhref"]);
      if (href === null) pushText(m[0]);
      else out.push({ t: "link", href, text: g["ltext"] });
    } else if (g["url"] !== undefined) {
      const href = safeHref(g["url"]);
      if (href === null) pushText(m[0]);
      else out.push({ t: "link", href, text: g["url"] });
    } else if (g["strong"] !== undefined) out.push({ t: "strong", c: parseInline(g["strong"]) });
    else if (g["em1"] !== undefined) out.push({ t: "em", c: parseInline(g["em1"]) });
    else if (g["em2"] !== undefined) out.push({ t: "em", c: parseInline(g["em2"]) });
    else if (g["mention"] !== undefined) out.push({ t: "mention", handle: g["mention"] });
    else pushText(m[0]);
  }
  pushText(text.slice(at));
  return out;
}

export function parseMarkdownLite(body: string): Block[] {
  const blocks: Block[] = [];
  const flush = (text: string) => {
    const trimmed = text.replace(/^\n+|\n+$/g, "");
    if (trimmed.length > 0) blocks.push({ t: "p", c: parseInline(trimmed) });
  };
  // Odd parts sit between fences; an unclosed fence stays literal text.
  const parts = body.split("```");
  let text = parts[0] ?? "";
  for (let i = 1; i < parts.length; i += 2) {
    const code = parts[i] ?? "";
    const after = parts[i + 1];
    if (after === undefined) {
      text += "```" + code;
      break;
    }
    flush(text);
    blocks.push({ t: "pre", v: code.replace(/^[\w-]*\n/, "").replace(/\n$/, "") });
    text = after;
  }
  flush(text);
  return blocks;
}

function renderInline(nodes: Inline[], isMe: (handle: string) => boolean): ReactNode[] {
  return nodes.map((n, i) => {
    switch (n.t) {
      case "text":
        return n.v;
      case "code":
        return (
          <code key={i} className="rounded bg-surface-2 px-1 py-0.5 font-mono text-[0.85em]">
            {n.v}
          </code>
        );
      case "strong":
        return <strong key={i}>{renderInline(n.c, isMe)}</strong>;
      case "em":
        return <em key={i}>{renderInline(n.c, isMe)}</em>;
      case "link":
        return (
          <a key={i} href={n.href} target="_blank" rel="noopener noreferrer nofollow" className="text-accent underline break-all">
            {n.text}
          </a>
        );
      case "mention":
        return (
          <span
            key={i}
            className={isMe(n.handle) ? "rounded bg-accent px-0.5 text-accent-contrast" : "rounded bg-surface-2 px-0.5 font-medium text-accent"}
          >
            @{n.handle}
          </span>
        );
    }
  });
}

/** Renders a chat body. `isMe` highlights mentions of the signed-in user. */
export function RichText({ body, isMe }: { body: string; isMe: (handle: string) => boolean }) {
  return (
    <div className="flex flex-col gap-1 text-sm text-text">
      {parseMarkdownLite(body).map((b, i) =>
        b.t === "pre" ? (
          <pre key={i} className="overflow-x-auto rounded-md bg-surface-2 p-2 font-mono text-xs">
            <code>{b.v}</code>
          </pre>
        ) : (
          <p key={i} className="whitespace-pre-wrap break-words">
            {renderInline(b.c, isMe)}
          </p>
        ),
      )}
    </div>
  );
}
